import React, { useEffect, useState } from 'react';
import { fetchGet } from '../../api/common';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useTheme,
} from '@mui/material';
import { DailyEtiquette } from '../../dialog/DailyEtiquette';
import {
  DailiesAuthor,
  DailiesStats,
  DailyData,
  DbQuestionData,
  DetailedPlayer,
  Maintenance,
  RoundScore,
} from '../room/types';
import { Scorecard } from '../scorecard';
import { DailyPlayersResults } from './DailyPlayersResults';
import { CommunityStats, DailiesQuestionStat } from './CommunityStats';
import { StyledPaperItem } from '../../common/mui-components';
import { DailiesProfile } from '../../dialog/DailiesProfile';
import { getAbbreviatedDayOfTheWeek, utcToLocalDate } from '../room/utils';
import InfoIcon from '@mui/icons-material/Info';
import { DailiesButton } from '../../common/components/buttons/DailiesButton';
import { Streaks } from './Streaks';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ScorecardMulti } from '../room/game/scorecard-multi';
import { dailiesThemes } from '@par/shared/components/dailies';
import { Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.root}`]: {
    position: 'relative',
    borderLeft: `1px solid #4c4e50`,
  },
}));

export type DailiesStreaks = {
  usersNoTimeOutTop: Winner[];
  usersPlayedTop: Winner[];
  usersParOrBetterTop: Winner[];
  usersNoTimeOutChasers: Winner[];
  usersPlayedChasers: Winner[];
  usersParOrBetterChasers: Winner[];
};

export type Winner = {
  avatarPublicId: string;
  dailiesStats: DailiesStats;
  profileId: string;
  username: string;
};

export type DailiesResults = {
  avatarPublicId: string;
  createdAt: number;
  profileId: string;
  score: number;
  username: string;
};

type MmrRangeOptions =
  | 'all'
  | '1000'
  | '1200'
  | '1400'
  | '1600'
  | '1800'
  | 'calibrating';

type PropTypes = {
  connected?: boolean;
  dailiesAuthor?: DailiesAuthor;
  dailyData: DailyData;
  handleDailyCountdownStop: () => void;
  handlePlayDailyClick: (
    dailySet: number,
    dailyTheme?: string,
    missedDaily?: boolean
  ) => void;
  lastDailySetPlayedCache: number;
  maintenance: Maintenance;
  profile: Profile;
};

export const Dailies = ({
  connected,
  dailiesAuthor,
  dailyData,
  handleDailyCountdownStop,
  handlePlayDailyClick,
  lastDailySetPlayedCache,
  maintenance,
  profile,
}: PropTypes) => {
  const [dailiesQuestionStats, setDailiesQuestionStats] =
    useState<DailiesQuestionStat[]>();
  const [dailiesResults, setDailiesResults] = useState<DailiesResults[]>();
  const [dailySetsPlayed, setDailySetsPlayed] = useState<number[]>();
  const [dailyGame, setDailyGame] = useState<{
    game: Game;
    questionSet: DbQuestionData[];
  }>();
  const [openDailyEtiquette, setOpenDailyEtiquette] = useState(false);
  const [playerDailyScores, setPlayerDailyScores] = useState<{
    scores: Record<number, RoundScore>;
    missedDaily: boolean;
  }>();
  const [openDailiesProfileModal, setOpenDailiesProfileModal] = useState(false);
  const [dailySet, setDailySet] = useState(dailyData.currentDailySet);
  const [pastDailySetList, setPastDailySetList] =
    useState<{ set: number; dayOfWeek: string }[]>();
  const [missedDailySetList, setMissedDailySetList] =
    useState<{ set: number; dayOfWeek: string }[]>();
  const [selectMissedDailySet, setSelectMissedDailySet] = useState(0);
  const [dailiesStreaks, setDailiesStreaks] = useState<DailiesStreaks>();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [days, setDays] = useState([
    { key: 0, label: 'Mon' },
    { key: 1, label: 'Tue' },
    { key: 2, label: 'Wed' },
    { key: 3, label: 'Thu' },
    { key: 4, label: 'Fri' },
    { key: 5, label: 'Sat' },
    { key: 6, label: 'Sun' },
  ]);
  const [dayOfWeekAbbr, setDayOfWeekAbbr] = useState('Sun');
  const [selecteMmrRange, setSelecteMmrRange] =
    useState<MmrRangeOptions>('all');

  const theme = useTheme();

  useEffect(() => {
    setDailiesQuestionStats(undefined);
    setDailiesResults(undefined);
    setPlayerDailyScores(undefined);
    setDailyGame(undefined);
    fetchDailiesQuestionStats();
    fetchDailiesResults();
    fetchPlayerDailiesResult();
    fetchDailyGame();
    fetchDailySetsPlayed();
    fetchDailiesStreaks();
  }, [dailySet]);

  useEffect(() => {
    if (!dailySetsPlayed) return;
    const pastDailySets: { set: number; dayOfWeek: string }[] = [];
    const missedDailySets: { set: number; dayOfWeek: string }[] = [];
    const currentDailyDate = new Date(dailyData.nextDailySchedule);
    const pastDailyDate = new Date(dailyData.nextDailySchedule);
    currentDailyDate.setUTCDate(currentDailyDate.getUTCDate() - 1); // -2 to skip current (today)
    pastDailyDate.setUTCDate(pastDailyDate.getUTCDate() - 1); // -2 to skip current (today)
    for (let i = dailyData.currentDailySet - 1; i > 0; i--) {
      currentDailyDate.setDate(currentDailyDate.getDate() - 1);
      if (!dailySetsPlayed.includes(i)) {
        missedDailySets.push({
          set: i,
          dayOfWeek: getAbbreviatedDayOfTheWeek(currentDailyDate.getTime()),
        });
      }
      if (i === dailyData.currentDailySet - 1) continue;
      pastDailyDate.setDate(pastDailyDate.getDate() - 1);
      pastDailySets.push({
        set: i,
        dayOfWeek: getAbbreviatedDayOfTheWeek(pastDailyDate.getTime()),
      });
    }
    setMissedDailySetList(missedDailySets);
    setPastDailySetList(pastDailySets);
  }, [dailySetsPlayed]);

  useEffect(() => {
    if (!dailyData) return;
    const d = new Date(dailyData.nextDailySchedule);
    d.setDate(d.getDate() - 1);
    const dayOfWeek = getAbbreviatedDayOfTheWeek(d.getTime());
    setDayOfWeekAbbr(dayOfWeek);
  }, [dailyData]);

  const fetchDailiesQuestionStats = async (low?: number, high?: number) => {
    let res: Record<number, DailiesQuestionStat>;
    if (low && high) {
      res = await fetchGet(
        `/dailies/dailies-question-stats/${dailySet}?lowMmr=${low}&highMmr=${high}`
      );
    } else {
      res = await fetchGet(`/dailies/dailies-question-stats/${dailySet}`);
    }
    setDailiesQuestionStats(Object.values(res));
  };

  const fetchDailySetsPlayed = async () => {
    const res: number[] = await fetchGet(
      `/dailies/daily-sets-played/${profile.id}`
    );
    setDailySetsPlayed(res);
  };

  const fetchDailiesResults = async (low?: number, high?: number) => {
    let res: DailiesResults[];
    if (low && high) {
      res = await fetchGet(
        `/dailies/dailies-results/${dailySet}?lowMmr=${low}&highMmr=${high}`
      );
    } else {
      res = await fetchGet(`/dailies/dailies-results/${dailySet}`);
    }
    setDailiesResults(res);
  };

  const fetchPlayerDailiesResult = async () => {
    const res: { scores: Record<number, RoundScore>; missedDaily: boolean } =
      await fetchGet(`/dailies/dailies-result/${profile.id}/${dailySet}`);
    if (!res.scores) return;
    setPlayerDailyScores(res);
  };

  const fetchDailyGame = async () => {
    const res: { game: Game; questionSet: DbQuestionData[] } = await fetchGet(
      `/dailies/current-daily/${profile.id}/${dailySet}`
    );
    if (!res.game) return;
    setDailyGame(res);
  };

  const fetchDailiesStreaks = async () => {
    const res = await fetchGet(`/dailies/dailies-streaks/`);
    setDailiesStreaks(res);
  };

  const handleDailyEtiquetteClick = () => setOpenDailyEtiquette(true);
  const handleDailiesClick = () => setOpenDailiesProfileModal(true);

  const handleDailySetChange = (event: SelectChangeEvent) => {
    setDailySet(+event.target.value);
  };

  const handleSelectMissedDailySetChange = (event: SelectChangeEvent) => {
    setSelectMissedDailySet(+event.target.value);
  };

  const handlePlayMissedDailiesClick = () => {
    const missedDaily = missedDailySetList?.find(
      (e) => e.set === selectMissedDailySet
    );
    const dailyTheme = missedDaily
      ? dailiesThemes[missedDaily.dayOfWeek].theme
      : undefined;
    handlePlayDailyClick(selectMissedDailySet, dailyTheme, true);
  };

  const disablePlayYourDaily = () =>
    profile.lastDailySetPlayed >= dailyData.currentDailySet ||
    lastDailySetPlayedCache >= dailyData.currentDailySet;

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleMmrRangeChange = (e: any) => {
    const value = e.target.value as MmrRangeOptions;
    setSelecteMmrRange(value);
    if (value === 'all') {
      fetchDailiesQuestionStats();
      fetchDailiesResults();
    } else if (value === '1000') {
      fetchDailiesQuestionStats(100, 1199);
      fetchDailiesResults(100, 1199);
    } else if (value === '1200') {
      fetchDailiesQuestionStats(1200, 1399);
      fetchDailiesResults(1200, 1399);
    } else if (value === '1400') {
      fetchDailiesQuestionStats(1400, 1599);
      fetchDailiesResults(1400, 1599);
    } else if (value === '1600') {
      fetchDailiesQuestionStats(1600, 1799);
      fetchDailiesResults(1600, 1799);
    } else if (value === '1800') {
      fetchDailiesQuestionStats(1800, 2500);
      fetchDailiesResults(1800, 2500);
    } else if (value === 'calibrating') {
      fetchDailiesQuestionStats(1, 1);
      fetchDailiesResults(1, 1);
    }
  };

  const themeTooltip = (description: string) => {
    return <Typography variant="caption">{description}</Typography>;
  };

  return (
    <>
      <DailiesProfile
        closeDailiesProfile={() => setOpenDailiesProfileModal(false)}
        openDailiesProfile={openDailiesProfileModal}
        viewProfile={profile}
      ></DailiesProfile>
      <DailyEtiquette
        closeDailyEtiquette={() => setOpenDailyEtiquette(false)}
        openDailyEtiquette={openDailyEtiquette}
      ></DailyEtiquette>
      <Container
        sx={{
          position: 'relative',
          flexGrow: 1,
          marginTop: 2,
          paddingBottom: '100px',
        }}
      >
        <Stack
          alignItems="baseline"
          direction="row"
          justifyContent="space-between"
          mb={2}
        >
          <Box width="82px"></Box>
          <Typography
            variant="h3"
            textAlign="center"
            sx={{
              color:
                theme.palette.mode === 'light'
                  ? theme.palette.dailies.main
                  : theme.palette.text.primary,
              textShadow:
                theme.palette.mode === 'dark'
                  ? `0 0 1px ${theme.palette.dailies.main}, 10px 0px 20px ${theme.palette.dailies.main}, 0px 0px 16px ${theme.palette.dailies.main}`
                  : 'none',
            }}
          >
            Dailies
          </Typography>
          <Button startIcon={<InfoIcon />} onClick={handleDailyEtiquetteClick}>
            About
          </Button>
        </Stack>
        <Stack
          direction={'row'}
          spacing={2}
          mb={2}
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {days.map((data) => {
            return (
              <Tooltip
                title={themeTooltip(dailiesThemes[data.label].description)}
                placement="top"
              >
                <Chip
                  variant={'outlined'}
                  label={
                    dayOfWeekAbbr === data.label
                      ? dailiesThemes[dayOfWeekAbbr].theme
                      : data.label
                  }
                  sx={{
                    borderColor:
                      dayOfWeekAbbr === data.label
                        ? theme.palette.mode === 'light'
                          ? theme.palette.dailies.main
                          : theme.palette.text.primary
                        : 'none',
                    color:
                      theme.palette.mode === 'light'
                        ? theme.palette.dailies.main
                        : theme.palette.text.primary,
                  }}
                />
              </Tooltip>
            );
          })}
        </Stack>
        <Stack
          direction={'row'}
          spacing={1}
          justifyContent={'center'}
          alignItems={'center'}
          sx={{
            position: 'relative',
            padding: 1,
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme.palette.warning.light,
            mb: 2,
          }}
        >
          <ReportGmailerrorredIcon
            color="warning"
            sx={{ position: 'absolute', top: 5, left: 5 }}
          />
          <Stack spacing={2} justifyContent={'center'}>
            <Typography textAlign={'center'} color="warning.light">
              Desktop Dailies have been discontinued
            </Typography>
            <Typography textAlign={'center'} color="warning.light">
              You can still play past dailies and compare your results to the
              community
            </Typography>
            <Typography textAlign={'center'} color="warning.light">
              Click in the "Play missed Dailies" section
            </Typography>
          </Stack>
        </Stack>
        <Grid container spacing={2} className="CONTAINER">
          <Grid item xs={12} sm={12} className="HEADER-STUFF">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} className="HEADER-LEFT">
                <Stack height="100%" width="100%" spacing={3}>
                  {/* <DailiesButton
                    connected={connected}
                    dailiesAuthor={dailiesAuthor}
                    dailyData={dailyData}
                    handleDailyCountdownStop={handleDailyCountdownStop}
                    handlePlayDailyClick={handlePlayDailyClick}
                    lastDailySetPlayedCache={lastDailySetPlayedCache}
                    maintenance={maintenance}
                    profile={profile}
                  /> */}
                  <StyledPaperItem style={{ flex: 1, justifySelf: 'center' }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{
                        height: '100%',
                        fontSize: 20,
                        letterSpacing: { xs: 2, sm: 4 },
                      }}
                      onClick={handleDailiesClick}
                    >
                      YOUR DAILIES SUMMARY
                    </Button>
                  </StyledPaperItem>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={4} className="HEADER-MIDDLE">
                <StyledPaperItem style={{ height: '100%' }}>
                  <Stack
                    alignItems="center"
                    display="grid"
                    justifyContent="center"
                    height="100%"
                  >
                    <Typography mb={1}>
                      {dailySet === dailyData.currentDailySet
                        ? `Today's Daily has been played by`
                        : `Past Daily Set #${dailySet} was played by`}
                    </Typography>
                    <Typography
                      flex={1}
                      justifySelf="center"
                      variant="h3"
                      color={
                        theme.palette.mode === 'light'
                          ? theme.palette.dailies.main
                          : theme.palette.text.primary
                      }
                      sx={{
                        textShadow:
                          theme.palette.mode === 'dark'
                            ? `0 0 0px ${theme.palette.dailies.main}, 0 0 0px ${theme.palette.dailies.main}, 0px 0px 16px ${theme.palette.dailies.main}`
                            : 'none',
                      }}
                    >
                      {dailiesResults?.length}
                    </Typography>
                    <Typography variant="body1" mt={1}>
                      player{dailiesResults?.length === 1 ? '' : 's'}
                      {selecteMmrRange !== 'all'
                        ? ' (filtered by MMR range)'
                        : ''}
                      {dailySet !== dailyData.currentDailySet &&
                      dailiesResults &&
                      dailiesResults.length > 0
                        ? ' on ' + utcToLocalDate(dailiesResults[0].createdAt)
                        : ''}
                    </Typography>
                  </Stack>
                </StyledPaperItem>
              </Grid>

              <Grid item xs={12} sm={4} className="HEADER-RIGHT">
                <Stack spacing={2} height="100%" display="grid">
                  <StyledPaperItem>
                    <Stack
                      alignItems="center"
                      direction="row"
                      height="100%"
                      justifyContent="center"
                      spacing={2}
                    >
                      <Typography>View Daily</Typography>
                      <FormControl
                        variant="standard"
                        sx={{ m: 1, minWidth: 120 }}
                        size="small"
                      >
                        <Select
                          value={dailySet.toString()}
                          onChange={handleDailySetChange}
                        >
                          <MenuItem value={dailyData.currentDailySet}>
                            #{dailyData.currentDailySet} (today)
                          </MenuItem>
                          {pastDailySetList?.map((o) => (
                            <MenuItem value={o.set + 1}>
                              #{o.set + 1} {o.dayOfWeek}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </StyledPaperItem>
                  <StyledPaperItem
                    style={{ flex: 1, justifySelf: 'center', width: '100%' }}
                  >
                    <Stack
                      height="100%"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography mb={2}>Play missed Dailies</Typography>
                      <Stack
                        alignItems="center"
                        direction="row"
                        justifyContent="center"
                        spacing={1}
                      >
                        <FormControl
                          variant="standard"
                          sx={{ m: 1, minWidth: 80 }}
                          size="small"
                        >
                          <Select
                            value={selectMissedDailySet?.toString() || '0'}
                            onChange={handleSelectMissedDailySetChange}
                          >
                            <MenuItem disabled value="0">
                              <em>select</em>
                            </MenuItem>
                            {missedDailySetList?.map((o) => (
                              <MenuItem value={o.set}>
                                #{o.set} {o.dayOfWeek}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handlePlayMissedDailiesClick}
                          disabled={
                            selectMissedDailySet === 0 || maintenance.scheduled
                          }
                        >
                          PLAY
                        </Button>
                      </Stack>
                    </Stack>
                  </StyledPaperItem>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} className="STREAKS">
            <Accordion
              expanded={expanded === 'streaks'}
              onChange={handleAccordionChange('streaks')}
              sx={{
                border: '1px solid',
                borderColor: theme.palette.primary.main,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    justifyContent: 'center',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      theme.palette.mode === 'light'
                        ? theme.palette.dailies.main
                        : theme.palette.text.primary,
                    textShadow:
                      theme.palette.mode === 'dark'
                        ? `0 0 1px ${theme.palette.dailies.main}, 10px 0px 20px ${theme.palette.dailies.main}, 0px 0px 16px ${theme.palette.dailies.main}`
                        : 'none',
                    letterSpacing: 10,
                    marginLeft: 5,
                  }}
                >
                  STREAKS
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {dailiesStreaks ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4} className="PLAYED">
                      <StyledPaperItem
                        style={{ height: '100%', width: '100%' }}
                      >
                        <Streaks
                          dailiesStreaks={dailiesStreaks}
                          streakCategory={'played'}
                        ></Streaks>
                      </StyledPaperItem>
                    </Grid>

                    <Grid item xs={12} sm={4} className="NO-TIMEOUTS">
                      <StyledPaperItem
                        style={{ height: '100%', width: '100%' }}
                      >
                        <Streaks
                          dailiesStreaks={dailiesStreaks}
                          streakCategory={'timeouts'}
                        ></Streaks>
                      </StyledPaperItem>
                    </Grid>

                    <Grid item xs={12} sm={4} className="SCRATCHES">
                      <StyledPaperItem
                        style={{ height: '100%', width: '100%' }}
                      >
                        <Streaks
                          dailiesStreaks={dailiesStreaks}
                          streakCategory={'scratches'}
                        ></Streaks>
                      </StyledPaperItem>
                    </Grid>
                  </Grid>
                ) : null}
              </AccordionDetails>
            </Accordion>
          </Grid>
          {playerDailyScores ||
          profile.id === dailiesAuthor?.authorProfileId ||
          profile.admin ? (
            <Grid item xs={12} sm={12} className="MMR-RANGES">
              <Box
                sx={{
                  marginBottom: 2,
                  paddingTop: 1,
                  paddingX: 1,
                  border: '1px solid',
                  borderColor: theme.palette.ranked.main,
                  borderRadius: '5px',
                }}
              >
                <Typography textAlign={'center'} fontWeight={600}>
                  Filter by MMR range
                </Typography>
                <Stack
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <FormControl>
                    <RadioGroup
                      row
                      defaultValue="all"
                      value={selecteMmrRange}
                      onChange={handleMmrRangeChange}
                    >
                      <Stack direction={'row'} spacing={4}>
                        <FormControlLabel
                          value="all"
                          control={<Radio />}
                          label="All"
                        />
                        <FormControlLabel
                          value="1000"
                          control={<Radio />}
                          label="Under 1200"
                        />
                        <FormControlLabel
                          value="1200"
                          control={<Radio />}
                          label="1200-1400"
                        />
                        <FormControlLabel
                          value="1400"
                          control={<Radio />}
                          label="1400-1600"
                        />
                        <FormControlLabel
                          value="1600"
                          control={<Radio />}
                          label="1600-1800"
                        />
                        <FormControlLabel
                          value="1800"
                          control={<Radio />}
                          label="Over 1800"
                        />
                        <FormControlLabel
                          value="calibrating"
                          control={<Radio />}
                          label="Unranked"
                        />
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                </Stack>
              </Box>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={6} className="COMMUNITY-STATS">
            <Grid item xs={12}>
              <StyledPaperItem>
                <CommunityStats
                  dailiesAuthor={dailiesAuthor}
                  dailiesQuestionStats={dailiesQuestionStats}
                  dailiesResults={dailiesResults}
                  isAdmin={profile.admin}
                  missedDaily={playerDailyScores?.missedDaily}
                  playerDailyScores={playerDailyScores?.scores}
                  profileId={profile.id}
                ></CommunityStats>
              </StyledPaperItem>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6} className="RESULTS">
            <Grid item xs={12}>
              <StyledPaperItem>
                <DailyPlayersResults
                  dailiesResults={dailiesResults}
                ></DailyPlayersResults>
              </StyledPaperItem>
            </Grid>
          </Grid>
          {dailyGame ? (
            <Grid item xs={12} sm={12}>
              <StyledPaperItem>
                <>
                  <Typography
                    variant="h5"
                    mb={2}
                    sx={{
                      textShadow:
                        theme.palette.mode === 'dark'
                          ? `0 0 0px ${theme.palette.dailies.main}, 0 0 0px ${theme.palette.dailies.main}, 0px 0px 16px ${theme.palette.dailies.main}`
                          : 'none',
                    }}
                  >
                    Your Daily Scorecard
                  </Typography>
                  <ScorecardMulti
                    detailedPlayers={
                      dailyGame.game.players as Record<string, DetailedPlayer>
                    }
                    isDaily
                    gameId={dailyGame.game.gameId}
                    hideTitle={true}
                    mode={'solo'}
                    questionSet={dailyGame.questionSet}
                  ></ScorecardMulti>
                </>
              </StyledPaperItem>
            </Grid>
          ) : null}
        </Grid>
      </Container>
    </>
  );
};
